import React from 'react';
import { connect } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import { MuiThemeProvider, createTheme } from '@material-ui/core';
import { resultsConfigSelector, quoteFormSelector } from 'ctm-selectors';
import { withRules, SimpleFlexbox, ModalCloseIcon } from 'ctm-components';
import moment from 'moment';
import { pageContext } from 'ctm-util-functions';
import styled from 'styled-components';

const theme = createTheme({
    overrides: {
        MuiPaper: {
            root: {
                padding: '2rem',
                // To fix the absolute-positioned close icon being cut off
                overflowY: 'unset !important'
            }
        }
    }
});

const Title = styled.h1`
    font-size: 1.6rem;
    color: var(--brand-primary);
    font-weight: 600;
    margin-bottom: 0.5rem;
    line-height: 20px;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1rem;
`;

const Text = styled.p`
    font-size: 1.4rem;
    line-height: 1.7rem;
    color: var(--brand-base);
    font-weight: 400;
    margin-bottom: 0.5rem;
`;

const Button = styled.button`
    background: var(--brand-base-0);
    border: ${(props) => props.border};
    box-sizing: border-box;
    border-radius: 4px;
    font-size: 1.4rem;
    font-weight: 500;
    color: var(--brand-primary);
    padding: 14px 24px;
    height: 4.4rem;
    &:hover {
        cursor: pointer;
    }
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12rem;
`;

const _UpdateQuoteModal = (props) => {
    const { UpdateQuoteModalIsOpen, closeModal, fetchNewResults, quoteModalRedirect, editDetailsRedirect, resetHasSavedResults, homeExcessFilterValue, contentsExcessFilterValue, commencementDate } = props;
    const renderCloseIcon = () => {
        const closeIconProps = { onClickFunction: closeModal };
        return React.createElement(ModalCloseIcon, closeIconProps);
    };

    const browserHistory = pageContext.getProps().browserHistory;

    const handleRedirect = () => {
        const currentDate = moment().format('YYYY-MM-DD');
        const isCommencementDateInPast = moment(currentDate).isSameOrBefore(commencementDate);

        const redirectToCommencementDate = () => {
            closeModal();
            resetHasSavedResults(homeExcessFilterValue, contentsExcessFilterValue);
            pageContext.getProps().browserHistory.push('/home-contents-insurance/journey/commencement_date');
        };

        const redirectToQuoteModalRedirect = () => {
            closeModal();
            if (quoteModalRedirect) {
                editDetailsRedirect();
                browserHistory.push(quoteModalRedirect);
            } else if (!isCommencementDateInPast) {
                redirectToCommencementDate();
            } else {
                closeModal();
                fetchNewResults(homeExcessFilterValue, contentsExcessFilterValue);
            }
        };

        redirectToQuoteModalRedirect();
    };

    return (
        <MuiThemeProvider theme={theme}>
            <Dialog fullwidth open={UpdateQuoteModalIsOpen} disableBackdropClick disableEscapeKeyDown>
                <SimpleFlexbox display="flex" column>
                    <Title>Update quote details</Title>
                    <Text>This will replace your current quotes and prices may change.</Text>
                    <Text>You may be asked to update your policy start date if it is now in the past.</Text>
                </SimpleFlexbox>
                {renderCloseIcon()}
                <ButtonContainer>
                    <Button id="savedResults.ModalDialogueButton.cancel" onClick={closeModal} type="button">
                        Cancel
                    </Button>
                    <Button
                        id="savedResults.ModalDialogueButton.update"
                        type="button"
                        border={'1px solid var(--brand-primary)'}
                        onClick={() => {
                            handleRedirect();
                        }}
                    >
                        Update
                    </Button>
                </ButtonContainer>
            </Dialog>
        </MuiThemeProvider>
    );
};

const mapDispatchToProps = (dispatch) => ({

    resetHasSavedResults: (homeExcess, contentsExcess) => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'SET_VALUE',
            identifier: 'excessDetails.homeExcess',
            stateKey: 'vertical.quote',
            value: homeExcess
        });
        dispatch({
            type: 'SET_VALUE',
            identifier: 'excessDetails.contentsExcess',
            stateKey: 'vertical.quote',
            value: contentsExcess
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.homeExcess',
            value: homeExcess
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.contentsExcess',
            value: contentsExcess
        });
        dispatch({
            type: 'SET_FILTER_ACTION',
            resultsStateKey: 'filters.homeExcess',
            resultsStagedStateKey: 'helpers.filters.homeExcess',
            formStateKey: 'form.excessDetails.homeExcess'
        });
        dispatch({
            type: 'SET_FILTER_ACTION',
            resultsStateKey: 'filters.contentsExcess',
            resultsStagedStateKey: 'helpers.filters.contentsExcess',
            formStateKey: 'form.excessDetails.contentsExcess'
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.fetchNewResults',
            value: true
        });
    },

    fetchNewResults: (homeExcess, contentsExcess) => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.fetchNewResults',
            value: true
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'lastViewedPage',
            value: 'results'
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.fetchedNewResults',
            value: true
        });

        dispatch({
            type: 'SET_VALUE',
            identifier: 'excessDetails.homeExcess',
            stateKey: 'vertical.quote',
            value: homeExcess
        });
        dispatch({
            type: 'SET_VALUE',
            identifier: 'excessDetails.contentsExcess',
            stateKey: 'vertical.quote',
            value: contentsExcess
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.homeExcess',
            value: homeExcess
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'filters.contentsExcess',
            value: contentsExcess
        });
        dispatch({
            type: 'SET_FILTER_ACTION',
            resultsStateKey: 'filters.homeExcess',
            resultsStagedStateKey: 'helpers.filters.homeExcess',
            formStateKey: 'form.excessDetails.homeExcess'
        });
        dispatch({
            type: 'SET_FILTER_ACTION',
            resultsStateKey: 'filters.contentsExcess',
            resultsStagedStateKey: 'helpers.filters.contentsExcess',
            formStateKey: 'form.excessDetails.contentsExcess'
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'doReload',
            value: true
        });
    },

    editDetailsRedirect: () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.fetchedNewResults',
            value: true
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasSeenResults',
            value: true
        });
    }
});

const mapStateToProps = (state) => {
    const isModalOpen = resultsConfigSelector(state).getIn(['helpers', 'updateQuoteModal']);
    const quoteModalRedirect = resultsConfigSelector(state).getIn(['helpers', 'quoteModalRedirect']);

    const form = quoteFormSelector(state);
    const homeExcessFilterValue = form.getIn(['excessDetails', 'homeExcess']).toString();
    const contentsExcessFilterValue = form.getIn(['excessDetails', 'contentsExcess']).toString();
    const commencementDate = form.getIn(['coverDetails', 'commencementDate']);

    return { UpdateQuoteModalIsOpen: isModalOpen ? isModalOpen.open : false, quoteModalRedirect, homeExcessFilterValue, contentsExcessFilterValue, commencementDate };
};

const UpdateQuoteModal = withRules(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(_UpdateQuoteModal)
);

export { _UpdateQuoteModal, UpdateQuoteModal };
